import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const PAGE_SIZES = [10, 25, 50];

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);

  const [detailModal, setDetailModal] = useState(null); // single payment row

  const fetchPayments = useCallback(async (page = 1, limit = 10, q = '') => {
    try {
      const { data } = await api.get('/payment/admin/payments', { params: { page, limit, search: q } });
      setPayments(data.payments);
      setPagination(data.pagination);
    } catch {}
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      fetchPayments(1, pagination.limit, val);
    }, 400);
  };

  const goToPage = (p) => fetchPayments(p, pagination.limit, search);
  const changeLimit = (l) => fetchPayments(1, l, search);

  const openDetail = (payment) => setDetailModal(payment);

  const { page, totalPages, total, limit } = pagination;
  const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRow = Math.min(page * limit, total);
  const pageNums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) pageNums.push(i);

  return (
    <div>
      <div className="table-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span className="card-title">💳 Payments</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={st.label}>Show</span>
              <select style={{ ...st.input, padding: '7px 10px', cursor: 'pointer' }}
                value={limit} onChange={(e) => changeLimit(Number(e.target.value))}>
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ position: 'relative' }}>
              <span style={st.searchIcon}>🔍</span>
              <input
                style={{ ...st.input, paddingLeft: 30, width: 220 }}
                placeholder="Search name or email..."
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Currency</th>
                <th>Status</th>
                <th>Date</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#a0aec0', padding: 32 }}>No payments found</td></tr>
              ) : payments.map((p, i) => (
                <tr key={p.id}>
                  <td>{startRow + i}</td>
                  <td>{p.user?.name || '—'}</td>
                  <td>{p.user?.email || '—'}</td>
                  <td>{p.plan?.name || '—'}</td>
                  <td>{p.amount}</td>
                  <td>{p.currency}</td>
                  <td>
                    <span style={{
                      display: 'inline-block', borderRadius: 4, padding: '2px 8px', fontSize: 12,
                      background: p.status === 'paid' ? '#dcfce7' : '#fee2e2',
                      color: p.status === 'paid' ? '#16a34a' : '#dc2626',
                    }}>{p.status}</span>
                  </td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="edit-btn" onClick={() => openDetail(p)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 0 && (
          <div style={st.paginationWrap}>
            <span style={st.pageInfo}>Showing {startRow}–{endRow} of {total}</span>
            <div style={st.pageButtons}>
              <button style={st.pageBtn} disabled={page === 1} onClick={() => goToPage(1)}>«</button>
              <button style={st.pageBtn} disabled={page === 1} onClick={() => goToPage(page - 1)}>‹</button>
              {pageNums.map(n => (
                <button key={n} style={{ ...st.pageBtn, ...(n === page ? st.pageBtnActive : {}) }}
                  onClick={() => goToPage(n)}>{n}</button>
              ))}
              <button style={st.pageBtn} disabled={page === totalPages} onClick={() => goToPage(page + 1)}>›</button>
              <button style={st.pageBtn} disabled={page === totalPages} onClick={() => goToPage(totalPages)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Detail Modal */}
      {detailModal && (
        <div style={st.backdrop} onClick={() => setDetailModal(null)}>
          <div style={st.modalBox} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={st.modalHeader}>
              <span style={st.modalTitle}>💳 Payment Detail</span>
              <button style={st.closeBtn} onClick={() => setDetailModal(null)}>✕</button>
            </div>

            <div style={{ padding: '20px 24px 24px' }}>

              {/* Status banner */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: detailModal.status === 'paid' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${detailModal.status === 'paid' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
                borderRadius: 10, padding: '14px 18px', marginBottom: 20,
              }}>
                <span style={{ fontSize: 28 }}>{detailModal.status === 'paid' ? '✅' : '❌'}</span>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: detailModal.status === 'paid' ? '#34d399' : '#f87171' }}>
                    {detailModal.currency} {detailModal.amount}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {detailModal.status === 'paid' ? 'Payment Successful' : 'Payment Failed'}
                  </div>
                </div>
                <span style={{
                  marginLeft: 'auto', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: detailModal.status === 'paid' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)',
                  color: detailModal.status === 'paid' ? '#34d399' : '#f87171',
                  border: `1px solid ${detailModal.status === 'paid' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.2)'}`,
                }}>{detailModal.status.toUpperCase()}</span>
              </div>

              {/* Two-column info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  { label: '👤 Customer', value: detailModal.user?.name || '—' },
                  { label: '📧 Email',    value: detailModal.user?.email || '—' },
                  { label: '📦 Plan',     value: detailModal.plan?.name || '—' },
                  { label: '📅 Date',     value: new Date(detailModal.created_at).toLocaleString() },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500, wordBreak: 'break-all' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Session ID full width */}
              <div style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>🔑 Session ID</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{detailModal.stripe_session_id}</div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  label:          { fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },
  input:          { padding: '8px 12px', background: '#1e2340', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, fontSize: 13, outline: 'none', color: 'var(--text-primary)' },
  searchIcon:     { position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, pointerEvents: 'none', color: 'var(--text-muted)' },
  paginationWrap: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' },
  pageInfo:       { fontSize: 13, color: 'var(--text-muted)' },
  pageButtons:    { display: 'flex', gap: 4 },
  pageBtn:        { minWidth: 32, height: 32, padding: '0 10px', border: '1px solid rgba(255,255,255,0.15)', background: '#1e2340', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#cbd5e1' },
  pageBtnActive:  { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)', fontWeight: 600 },
  backdrop:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalBox:       { background: 'var(--bg-card)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  modalHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 },
  modalTitle:     { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  closeBtn:       { background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', fontSize: 14, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, borderRadius: 6, padding: '4px 8px' },
};
